import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Col, Container, Nav, Row, Spinner, Tab } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../../../Hooks/usePageTitle';
import { getLikesMe, getMyLikes, getViewedMe } from '../../../Services/User/Likes';

import InnerBanner from '../../../Components/InnerBanner/InnerBanner';
import './Likes.css';
import { FaUser } from "react-icons/fa";

const Likes = () => {
  usePageTitle('Likes');
  const [activeTab, setActiveTab] = useState('likes-me');
  const navigate = useNavigate();

  const { data: likesMe, isLoading: isLoadingLikesMe } = useQuery({
    queryKey: ['likes-me'],
    queryFn: getLikesMe,
    enabled: activeTab === 'likes-me',
  });

  const { data: myLikes, isLoading: isLoadingMyLikes } = useQuery({
    queryKey: ['my-likes'],
    queryFn: getMyLikes,
    enabled: activeTab === 'my-likes',
  });

  const { data: viewedMe, isLoading: isLoadingViewedMe } = useQuery({
    queryKey: ['viewed-me'],
    queryFn: getViewedMe,
    enabled: activeTab === 'viewed-me',
  });


  const UserImage = ({ image, alt, ...props }) => {
    const [imgError, setImgError] = useState(false);

    const getImageUrl = (image) => {
      if (!image) return null;
      if (image.startsWith('http')) return image;
      return `${import.meta.env.VITE_REACT_APP_BASE_URL || ''}/storage/${image}`;
    };

    if (!image || imgError) {
      return <FaUser size={30} />;
    }

    return (
      <img
        src={getImageUrl(image)}
        alt={alt}
        onError={() => setImgError(true)}
        {...props}
      />
    );
  };

  const getTimeAgo = (date) => {
    const minutes = Math.floor((new Date() - new Date(date)) / 60000);
    if (minutes < 60) return `${minutes} Min Ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} Hours Ago`;
    const days = Math.floor(hours / 24);
    return `${days} Days Ago`;
  };

  const renderUserList = (data, isLoading) => {
    if (isLoading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading...</p>
        </div>
      );
    }

    if (!data?.data || data.data.length === 0) {
      return (
        <div className="text-center py-5">
          <p className="mt-3">No users found</p>
        </div>
      );
    }

    return (
      <div className="likes-list">
        {data.data.map((item) => (
          <div key={item.id} className="likes-item d-flex align-items-center justify-content-between py-3 px-2">
            <div className="d-flex align-items-center gap-3">
              <div className="user-image-wrapper rounded-circle d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', overflow: 'hidden', backgroundColor: '#f0f0f0' }}>
                <UserImage
                  image={item.target?.image}
                  alt={item.target?.username || item.target?.full_name}
                  className="rounded-circle"
                  style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                />
              </div>
              <span className="fw-semibold">
                {item.target?.username || item.target?.full_name || 'Unknown User'}
              </span>
            </div>
            <span className="text-muted small">{getTimeAgo(item.created_at)}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <InnerBanner heading="Likes" />
      <section className="py-sm-5 py-4">
        <Container>
          <Row>
            <Col xs={12}>
              <div className="likes-container site-card">
                <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                  <Nav variant="pills" className="likes-tabs mb-4">
                    <Nav.Item>
                      <Nav.Link eventKey="likes-me">Likes Me</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="my-likes">My Likes</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="viewed-me">Viewed Me</Nav.Link>
                    </Nav.Item>
                  </Nav>

                  <Tab.Content>
                    <Tab.Pane eventKey="likes-me">
                      {renderUserList(likesMe, isLoadingLikesMe)}
                    </Tab.Pane>

                    <Tab.Pane eventKey="my-likes">
                      {renderUserList(myLikes, isLoadingMyLikes)}
                    </Tab.Pane>

                    <Tab.Pane eventKey="viewed-me">
                      {renderUserList(viewedMe, isLoadingViewedMe)}
                    </Tab.Pane>
                  </Tab.Content>
                </Tab.Container>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default Likes;
